import Map from "ol/Map";
import MapBrowserEvent from "ol/MapBrowserEvent";
import View from "ol/View";
import {
  ReactNode,
  useEffect,
  useId,
  useState,
  useRef,
  useLayoutEffect,
} from "react";
import { defaults as defaultControls } from "ol/control";
import TileLayer from "ol/layer/Tile";
import { ImageStatic, OSM } from "ol/source";
import { fromLonLat } from "ol/proj";
import ImageLayer from "ol/layer/Image";
import { truncateNumber } from "../../../utils/truncateNumber";
import { useLazyRef } from "../hooks/useLazyRef";

/**
 * 이미지 픽셀 좌표 선택 props — `MapContainer` 와 완전히 독립된 컴포넌트.
 *
 * 지리 좌표가 아닌 픽셀 좌표(x,y)를 다룸. `useMap()` 등 MapContainer 기반 훅 사용 불가.
 */
interface ImageMapForPixelProps {
  /**
   * @default "1000px"
   */
  height?: string;

  imageSrc: string;

  /**
   * @default "1000px"
   */
  width?: string;
  fullscreenControl?: boolean;
  children?: ReactNode;

  onClick?: (pixel: { x: number; y: number }) => void;
}

export function ImageMapForPixel({
  imageSrc,
  width,
  height,
  fullscreenControl,
  children,
  onClick,
}: ImageMapForPixelProps) {
  const id = useId();
  const mapId = `react-openlayers-map-${id}`;
  // useRef(new Heavy()) 는 매 렌더마다 throwaway 인스턴스를 생성한다 — lazy-init 으로 최초 1회만 생성(D7).
  const mapObj = useLazyRef<Map>(
    () =>
      new Map({
        view: new View({
          center: fromLonLat([0, 0]),
        }),
        controls: defaultControls({
          zoom: false,
          rotate: true,
        }).extend([]),
        layers: [
          new TileLayer({
            source: new OSM({
              crossOrigin: "anonymous",
            }),
          }),
        ],
      })
  );

  const [imagePixel, setImagePixel] = useState<{ x: number; y: number } | null>(
    null
  );

  const [imgSize, setImgSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  // onClick 을 렌더 단계에서 직접 호출하면 상위 setState 루프를 유발할 수 있다 —
  // imagePixel 변경 시점에만 호출되도록 effect 로 옮긴다(D8). onClick 은 ref 로 받아
  // 매 렌더 재호출을 피한다.
  const onClickRef = useRef(onClick);
  onClickRef.current = onClick;

  useEffect(() => {
    if (imagePixel) {
      onClickRef.current?.(imagePixel);
    }
  }, [imagePixel]);

  function getImageDimensions(src: string) {
    const img = new Image();
    img.onload = function () {
      const width = img.width;
      const height = img.height;
      setImgSize({ width, height });
    };
    img.onerror = function () {
      console.error("이미지 로드 실패", src);
    };
    img.src = src;
  }

  useLayoutEffect(() => {
    getImageDimensions(imageSrc);
  }, [imageSrc]);

  useEffect(() => {
    const mapRef = mapObj.current;

    mapRef.setTarget(mapId);

    function getImagePixel(event: MapBrowserEvent<any>) {
      if (!imgSize) return;
      // 클릭한 포인트의 좌표를 얻음
      var clickedCoordinate = event.coordinate;

      // 포인트 좌표를 픽셀 좌표로 변환
      var clickedPixel = clickedCoordinate;

      const heightdigit = `${imgSize.height}`;
      const heightbycount = heightdigit.length - 2;
      const rightdigit = `${imgSize.width}`;
      const rightbycount = rightdigit.length - 2;
      // 픽셀 좌표 출력

      const xPosition = truncateNumber(
        clickedPixel[0] * 10 ** rightbycount,
        0
      );
      const yPosition = truncateNumber(
        imgSize.height - clickedPixel[1] * 10 ** heightbycount,
        0
      );

      if (
        Number(xPosition) <= imgSize.width &&
        Number(yPosition) <= imgSize.height &&
        Number(xPosition) > 0 &&
        Number(yPosition) > 0
      ) {
        setImagePixel({
          x: Number(xPosition),
          y: Number(yPosition),
        });
      }
    }

    mapRef.on("click", getImagePixel);

    return () => {
      mapRef.un("click", getImagePixel);
      mapRef.setTarget(undefined);
      mapRef.setLayers([]);
      mapRef.dispose();
    };
  }, [imgSize, mapId]);

  useEffect(() => {
    if (!imgSize) return;
    const mapRef = mapObj.current;

    // image.height는 3648
    const heightdigit = `${imgSize.height}`;
    const heightbycount = heightdigit.length - 2;

    const bottomPosition = Number(
      truncateNumber(imgSize.height / 10 ** heightbycount, heightbycount)
    );

    // image.width는 5472
    const rightdigit = `${imgSize.width}`;
    const rightbycount = rightdigit.length - 2;
    const rightPosition = Number(
      truncateNumber(imgSize.width / 10 ** rightbycount, rightbycount)
    );

    const imageLayer = new ImageLayer({
      source: new ImageStatic({
        url: imageSrc,
        imageExtent: [0, 0, rightPosition, bottomPosition],
      }),
    });

    mapRef.addLayer(imageLayer);
    mapRef.getView().fit(imageLayer.getSource()?.getImageExtent()!, {
      duration: 1000, // 애니메이션 지속 시간 (밀리초)
    });
    return () => {
      mapRef.removeLayer(imageLayer);
    };
  }, [imageSrc, imgSize]);

  return (
    <div
      id={mapId}
      className="react-openlayers-map-container"
      style={{ width, height }}
    >
      {children}
    </div>
  );
}
